import { GameState, GameAction, CardDef, PlayerSide } from '../types.js';
import {
  getEligibleAttackers,
  hasPlayableCharacter,
  hasSupportableCombination,
  hasPlayableAction,
  canBlockAnyAttacker,
  hasPlayableReaction,
  mustDiscard,
} from '../engine/Rules.js';
import { getPlayerState } from '../engine/GameState.js';

interface Ledger {
  turnNumber: number;
  side: PlayerSide;
  main: {
    action: number;
    discard: number;
    attackSticky: boolean;
  };
  counterTurn: number;
  counter: {
    block: number;
    reaction: number;
  };
}

interface Task {
  text: string;
  complete: boolean;
}

const TASK_LIST_COLLAPSED_KEY = 'cardverse-task-list-collapsed';

function loadTaskListCollapsed(): boolean {
  try {
    return localStorage.getItem(TASK_LIST_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function saveTaskListCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(TASK_LIST_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch { /* ignore */ }
}

export class TaskList {
  private el: HTMLElement;
  private contentEl: HTMLElement;
  private catalog: Map<string, CardDef>;
  private ledger: Ledger | null = null;
  /** Total attackers staged this turn, snapshotted each `syncTurn` (which
   *  always runs right before `renderInto`). Drives the terminal
   *  Attack/End Turn task. */
  private stagedCount = 0;

  constructor(el: HTMLElement, catalog: Map<string, CardDef>) {
    this.el = el;
    this.catalog = catalog;

    // Persistent scaffolding: a toggle button + a content container that
    // renderInto rewrites. The button stays mounted across re-renders so its
    // click listener survives.
    this.el.innerHTML =
      '<button class="task-list-toggle" aria-label="Toggle tasks" type="button">Tasks</button>' +
      '<div class="task-list-content"></div>';
    this.contentEl = this.el.querySelector('.task-list-content') as HTMLElement;
    const toggle = this.el.querySelector('.task-list-toggle') as HTMLButtonElement;

    // Panel state persists across matches: new players see it open;
    // anyone who closes it stays closed until they open it again.
    // Missing key = open (the new-player default).
    if (loadTaskListCollapsed()) this.el.classList.add('collapsed');

    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      const collapsed = this.el.classList.toggle('collapsed');
      saveTaskListCollapsed(collapsed);
    });
  }

  // Called once per render: resets ledger on turn/side change and updates the
  // sticky attack-completion flag based on current staging.
  syncTurn(state: GameState, stagedAttackers: Map<string, string[]>): void {
    const turn = state.turnNumber;
    const side = state.currentPlayer;

    if (!this.ledger || this.ledger.turnNumber !== turn || this.ledger.side !== side) {
      this.ledger = {
        turnNumber: turn,
        side,
        main: { action: 0, discard: 0, attackSticky: false },
        counterTurn: -1,
        counter: { block: 0, reaction: 0 },
      };
    }

    if (state.phase === 'counter' && this.ledger.counterTurn !== turn) {
      this.ledger.counterTurn = turn;
      this.ledger.counter = { block: 0, reaction: 0 };
    }

    let stagedCount = 0;
    for (const [, members] of stagedAttackers) stagedCount += members.length;
    this.stagedCount = stagedCount;

    if (!this.ledger.main.attackSticky && state.currentPlayer === 'player' &&
        (state.phase === 'main' || state.phase === 'attack')) {
      const eligible = getEligibleAttackers(state);
      if (eligible.length > 0 && stagedCount >= eligible.length) {
        this.ledger.main.attackSticky = true;
      }
    }
  }

  onActionCommitted(action: GameAction): void {
    if (!this.ledger) return;
    switch (action.type) {
      case 'PLAY_ACTION': this.ledger.main.action++; break;
      case 'DISCARD_CARD': this.ledger.main.discard++; break;
      case 'ASSIGN_BLOCKER': this.ledger.counter.block++; break;
      case 'PLAY_REACTION': this.ledger.counter.reaction++; break;
    }
  }

  renderInto(state: GameState): void {
    const hidden = this.isHidden(state);
    this.el.classList.toggle('hidden', hidden);
    if (hidden) {
      this.contentEl.innerHTML = '';
      return;
    }

    const tasks = this.computeTasks(state);
    const header = this.headerText(state);
    this.contentEl.innerHTML =
      `<div class="task-list-header">${header}</div>` +
      `<ul class="task-items">${tasks.map(t =>
        `<li class="task${t.complete ? ' complete' : ''}"><span class="task-text">${t.text}</span></li>`,
      ).join('')}</ul>`;
  }

  private isHidden(state: GameState): boolean {
    if (state.winner) return true;
    const isPlayerTurn = state.currentPlayer === 'player';
    const isDefending = state.phase === 'counter' && state.currentPlayer === 'opponent';
    return !isPlayerTurn && !isDefending;
  }

  private headerText(state: GameState): string {
    if (state.phase === 'counter' && state.currentPlayer === 'opponent') return 'Counterattack';
    return 'Your turn';
  }

  private computeTasks(state: GameState): Task[] {
    const tasks: Task[] = [];
    if (!this.ledger) return tasks;

    const isDefending = state.phase === 'counter' && state.currentPlayer === 'opponent';
    if (isDefending) {
      this.pushRepeatable(
        tasks,
        this.ledger.counter.block,
        canBlockAnyAttacker(state, this.catalog),
        'Declare blockers',
        'Declare more blockers',
      );
      this.pushRepeatable(
        tasks,
        this.ledger.counter.reaction,
        hasPlayableReaction(state, this.catalog),
        'Play a reaction',
        'Play another reaction',
      );
      // Terminal: offer "Confirm Blocks" once the player has completed at
      // least one task this counter (declared a blocker / played a
      // reaction), so it doesn't nudge them to confirm before they've
      // engaged with blocking. When the player has no blockers and no
      // reactions at all, the counter phase auto-advances and this list
      // isn't interactive, so a blank panel there is fine and correct.
      if (tasks.some(t => t.complete)) {
        tasks.push({ text: 'Confirm Blocks', complete: false });
      }
      return tasks;
    }

    if (state.currentPlayer !== 'player') return tasks;

    const ps = getPlayerState(state, 'player');

    this.pushRepeatable(
      tasks,
      ps.ralliesUsed,
      hasPlayableCharacter(state, this.catalog),
      'Play a character',
      'Play another character',
    );

    this.pushRepeatable(
      tasks,
      ps.supportsUsed,
      hasSupportableCombination(state, this.catalog),
      'Support a character',
      'Support another character',
    );

    if (this.ledger.main.attackSticky) {
      tasks.push({ text: 'Choose attackers', complete: true });
    } else if (getEligibleAttackers(state).length > 0) {
      tasks.push({ text: 'Choose attackers', complete: false });
    }

    this.pushRepeatable(
      tasks,
      this.ledger.main.action,
      hasPlayableAction(state, this.catalog),
      'Play an action card',
      'Play another action card',
    );

    this.pushRepeatable(
      tasks,
      this.ledger.main.discard,
      mustDiscard(state, 'player', this.catalog) > 0,
      'Discard a card',
      'Discard another card',
    );

    // Terminal call-to-action, appended only when no other incomplete task
    // remains — don't nudge the player to commit the turn while they still
    // have plays to make. Main phase only (where the turn is committed):
    // "Attack" if attackers are staged, otherwise "End Turn".
    if (state.phase === 'main' && !tasks.some(t => !t.complete)) {
      tasks.push({ text: this.stagedCount > 0 ? 'Attack' : 'End Turn', complete: false });
    }

    return tasks;
  }

  /** Render a repeatable action as at most two lines:
   *    - available & never done   → firstLabel (incomplete)
   *    - done at least once        → firstLabel (complete), and then
   *        - still available       → repeatLabel (incomplete)
   *        - unavailable & done >1 → repeatLabel (complete)
   *
   *  `crossedCount` is a cumulative ledger that counts commits and never
   *  decrements, so an action stays crossed off after it's undone (e.g.
   *  unstaging a blocker keeps "Declare blockers" complete). Because this
   *  only ever emits ONE repeatLabel line, stage/unstage churn — which
   *  inflates the count — can't pile up duplicate lines; the count only
   *  decides the repeatLabel's complete/incomplete state, not how many
   *  lines appear. Uniform across blockers, supports, action cards, etc. */
  private pushRepeatable(
    out: Task[],
    crossedCount: number,
    available: boolean,
    firstLabel: string,
    repeatLabel: string,
  ): void {
    if (crossedCount === 0) {
      if (available) out.push({ text: firstLabel, complete: false });
      return;
    }
    out.push({ text: firstLabel, complete: true });
    if (available) {
      out.push({ text: repeatLabel, complete: false });
    } else if (crossedCount > 1) {
      out.push({ text: repeatLabel, complete: true });
    }
  }
}
